import React from 'react';
import { useAuth } from '../contexts/ClerkAuthContext';

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export class ApiClient {
  private getToken: (() => Promise<string | null>) | null = null;

  setTokenGetter(getTokenFn: () => Promise<string | null>) {
    this.getToken = getTokenFn;
  }

  private async fetch<T>(path: string, options: RequestInit = {}): Promise<T> {
    const headers = new Headers({
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> || {}),
    });

    if (this.getToken) {
      const token = await this.getToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
    }

    const response = await fetch(path, {
      ...options,
      headers,
    });

    if (response.status === 401) {
      // Dispatch a custom event that the auth context can listen for
      window.dispatchEvent(new Event('auth:unauthorized'));
      throw new ApiError(401, 'Your session has expired. Please sign in again.');
    }

    if (response.status === 403) {
      throw new ApiError(403, 'You do not have permission to access this resource.');
    }

    if (!response.ok) {
      throw new ApiError(response.status, 'API request failed');
    }

    return response.json();
  }

  async get<T>(path: string, headers?: Record<string, string>): Promise<T> {
    return this.fetch<T>(path, { headers });
  }

  async post<T>(path: string, data: any): Promise<T> {
    return this.fetch<T>(path, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async delete<T>(path: string, headers?: Record<string, string>): Promise<T> {
    return this.fetch<T>(path, {
      method: 'DELETE',
      headers,
    });
  }
}

export const apiClient = new ApiClient();

// Hook to set up the API client with Clerk authentication
export const useApiClient = () => {
  const { getToken } = useAuth();
  
  React.useEffect(() => {
    apiClient.setTokenGetter(getToken);
  }, [getToken]);

  return apiClient;
}; 